import { ArrowUpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePlan } from '../contexts/PlanContext';
import { api } from '../services/api';
import { Button } from './ui/button';

interface UpgradePromptProps {
  feature: string;
  title?: string;
  description?: string;
  className?: string;
}

const UpgradePrompt = ({ feature, title, description, className = '' }: UpgradePromptProps) => {
  const { t } = useTranslation('billing');
  const { plan } = usePlan();

  const upgradePlan = plan?.upgrade_plan;
  if (!upgradePlan) return null;

  const planName = upgradePlan === 'starter' ? t('starter_plan') : t('team_plan');

  const defaultDescriptions: Record<string, string> = {
    templates: t('template_limit_desc', { limit: plan?.limits.templates ?? 3 }),
    sms_region: t('sms_upgrade_desc'),
    custom_branding: t('branding_upgrade_desc'),
    team_invites: t('team_upgrade_desc'),
  };

  const handleUpgrade = async () => {
    try {
      const response = await api.post<{ url: string }>('/billing/checkout', {
        plan_slug: upgradePlan,
      });
      const url = (response as any).url || (response as any).data?.url;
      if (url) {
        window.location.href = url;
      }
    } catch (err) {
      console.error('Failed to create checkout session:', err);
    }
  };

  return (
    <div className={`rounded-lg border border-amber-200 bg-amber-50 p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <ArrowUpCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-amber-800">
            {title || t('upgrade_required')}
          </h4>
          <p className="text-sm text-amber-700 mt-1">
            {description || defaultDescriptions[feature] || t('upgrade_required')}
          </p>
          <Button
            size="sm"
            className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
            onClick={handleUpgrade}
          >
            <ArrowUpCircle className="h-4 w-4 mr-1.5" />
            {t('upgrade_to', { plan: planName })}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UpgradePrompt;
